/* global URL_API_REST */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getDeliveryPersonJwt, getDeliveryCommerceId } from './deliveryPersonAxios';
import { clearColdStartAutoReloaded, hasColdStartAutoReloaded, isInStartupWindow, waitForAuthReady } from './authReady';
import { handleStuckColdStart } from './coldStartReload';

const instance = axios.create({
  baseURL: URL_API_REST,
  timeout: 30 * 1000,
  headers: {},
});

// Cuántas veces reintentamos una request que salió en cold-start (sin token) tras esperar a
// que el token aparezca. Tope bajo y determinista para NO crear loops de reintento.
const MAX_COLD_START_RETRIES = 2;

// Reintentos para la clase transitoria REAL del cold-start (429/500/502/503/red). El 429 del
// authorizer throttleado y el 500 de API Gateway son la causa raíz probada del toast-storm:
// requests CON token que fallan en ráfaga. Solo reintentamos métodos idempotentes (GET) con
// backoff exponencial + jitter (un retry sin backoff empeora el throttle). Tope bajo: sin loops.
const MAX_TRANSIENT_RETRIES = 4;
const TRANSIENT_BACKOFF_BASE_MS = 500;
const TRANSIENT_BACKOFF_MAX_MS = 6_000;

// Cuántos endpoints DISTINTOS deben agotar sus reintentos dentro de la ventana de arranque para
// considerar el arranque "atascado" y disparar el último recurso (reload amigable / prompt manual).
// Un solo endpoint flaky NO es un arranque roto: `account-details` da 500 intermitentes (PRJ-008) y
// con umbral 1 ese único fallo secuestraba el panel entero — recarga completa y, al repetirse,
// modal bloqueante sobre una app que por lo demás funcionaba. "El panel no carga" es una condición
// global, así que la exigimos global.
const STUCK_ENDPOINTS_THRESHOLD = 2;

// Endpoints que ya agotaron reintentos en ESTA carga de página (module scope = se reinicia en cada
// arranque). Solo cuenta dentro de la ventana de arranque; la clave es método+URL estática, sin
// path dinámico ni params → sin PII y sin inflar el conteo con la misma ruta repetida.
const exhaustedStartupEndpoints = new Set<string>();

// Registra que una carga de arranque agotó sus reintentos y responde si ya hay evidencia suficiente
// (≥ `STUCK_ENDPOINTS_THRESHOLD` endpoints distintos) para declarar el arranque atascado.
const isStartupStuck = (config: InternalAxiosRequestConfig): boolean => {
  exhaustedStartupEndpoints.add(`${(config.method ?? 'get').toLowerCase()}:${config.url ?? ''}`);
  return exhaustedStartupEndpoints.size >= STUCK_ENDPOINTS_THRESHOLD;
};

// Status HTTP que tratamos como transitorios (vale la pena reintentar): throttle del authorizer
// (429) y fallos efímeros de gateway/upstream (500/502/503/504).
const TRANSIENT_STATUSES = new Set([429, 500, 502, 503, 504]);

// Marcadores internos que el interceptor de request deja en la config para que el de respuesta
// distinga un fallo TRANSITORIO de cold-start (request salió sin token) de un fallo REAL
// (request salió autenticada y aun así falló → sí debe informar).
interface ColdStartConfig extends InternalAxiosRequestConfig {
  /** La request salió SIN token (cold-start: el token aún no estaba en localStorage). */
  _sentWithoutAuth?: boolean;
  /** Reintentos de cold-start ya consumidos por esta request. */
  _coldStartRetries?: number;
  /** Reintentos transitorios (429/500/red) ya consumidos por esta request. */
  _transientRetries?: number;
}

/** Error de Axios etiquetado como transitorio de cold-start (los `.catch` pueden no mostrar toast). */
export type TransientAuthError = AxiosError & { isTransientAuthColdStart?: boolean };

// True si el método de la request es idempotente. Solo reintentamos GET (y HEAD): repetir un
// POST/PUT/PATCH/DELETE arriesga doble mutación. Las cargas de arranque que sufren el throttle
// son GET, así que no perdemos cobertura. Sin método explícito axios usa GET por defecto.
const isIdempotentMethod = (config?: InternalAxiosRequestConfig): boolean => {
  const method = (config?.method ?? 'get').toLowerCase();
  return method === 'get' || method === 'head';
};

// Backoff exponencial + jitter, acotado. `attempt` es 0-indexed (0 = primer reintento).
const transientBackoffMs = (attempt: number): number => {
  const exp = Math.min(TRANSIENT_BACKOFF_BASE_MS * 2 ** attempt, TRANSIENT_BACKOFF_MAX_MS);
  // Medio jitter [exp/2, exp]: desincroniza la ráfaga concurrente para no re-throttlear al
  // authorizer, pero garantiza una espera mínima. Con jitter COMPLETO ([0, exp]) los 3 reintentos
  // se consumían en ~1s de media: declarábamos el arranque "atascado" y recargábamos antes de que
  // el authorizer en frío tuviera tiempo de calentar, así que el segundo arranque fallaba igual.
  return exp / 2 + Math.random() * (exp / 2);
};

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

instance.interceptors.request.use(
  function (config) {
    const id_commerce = localStorage.getItem('id_commerce');
    const token = localStorage.getItem('token');

    // PLI-2031: Fallback al JWT del delivery_person en memoria
    const effectiveToken = token || getDeliveryPersonJwt();
    const effectiveCommerceId = id_commerce || getDeliveryCommerceId();

    if (effectiveToken && config.headers) {
      if (effectiveCommerceId) {
        (config.headers as unknown as { id_comercio: string }).id_comercio = effectiveCommerceId;
      }
      if (!config.headers.Authorization)
        (config.headers as unknown as { Authorization: string }).Authorization = `Bearer ${effectiveToken}`;
    }

    // Cold-start: marcamos la request que sale SIN token para que, si falla, el interceptor de
    // respuesta pueda esperar al token y reintentarla en vez de tratarla como un fallo real.
    (config as ColdStartConfig)._sentWithoutAuth = !effectiveToken;

    return config;
  },
  function (error) {
    return Promise.reject(error);
  },
);

/**
 * Interceptor de respuesta — recuperación transparente del cold-start (dos clases de fallo).
 *
 * Caso 1 — request SIN token (`_sentWithoutAuth`) que falla con 401/403 o red: la carga de mount
 * ganó la carrera contra `checkAuth`. Esperamos brevemente al token y REINTENTAMOS (acotado por
 * `MAX_COLD_START_RETRIES`). Si el token nunca llega, marcamos el error `isTransientAuthColdStart`
 * para que los `.catch` supriman el toast.
 *
 * Caso 2 — clase transitoria REAL (429/500/502/503/504 o red) en requests idempotentes (GET/HEAD),
 * con o sin token: es la causa raíz probada del toast-storm — la avalancha de mount-fetches en frío
 * throttlea al authorizer del panel (429) y API Gateway responde 500 en ráfaga. Reintentamos con
 * backoff exponencial + jitter (`MAX_TRANSIENT_RETRIES`); NUNCA reintentamos POST/PUT/PATCH/DELETE
 * (doble mutación). Si se agotan los reintentos DENTRO de la ventana de arranque y el atasco es
 * GLOBAL (`STUCK_ENDPOINTS_THRESHOLD` endpoints distintos agotados) → cold-start atascado → último
 * recurso: reload amigable loop-safe (`handleStuckColdStart`). Fuera de la ventana de arranque un
 * 500/429 puntual NO recarga: se propaga e informa como cualquier fallo.
 *
 * Distinción transitorio vs real:
 *   - SIN token + token llega → reintento → éxito (carga silenciosa).
 *   - Transitorio (429/500/red) en GET → reintento con backoff → éxito o, si persiste en arranque,
 *     reload amigable; en runtime se propaga e informa normal.
 *   - 401/403 CON token (sesión expirada) → NO se toca: fallo real → propaga y SÍ informa.
 *   - POST/PUT/PATCH/DELETE → NO se reintenta (idempotencia) → propaga intacto.
 *
 * No afecta el JWT del repartidor (el interceptor de request ya lo usa como `effectiveToken`; si
 * está presente, la request no se marca como cold-start). Idempotente y sin estado global.
 */
instance.interceptors.response.use(
  (response) => {
    // Arranque sano: solo limpiamos el guard del auto-reload una vez FUERA de la ventana de arranque.
    // Un éxito DENTRO de la ventana NO prueba que el arranque se recuperó (otras cargas críticas
    // pueden seguir fallando y dispararían un 2º auto-reload no deseado — hallazgo Seer). Ya pasada
    // la ventana (página sana >30s), un éxito sí significa recuperación → limpiamos para que un
    // cold-start FUTURO (próxima carga de página en esta pestaña) pueda auto-recuperarse una vez.
    if (!isInStartupWindow() && hasColdStartAutoReloaded()) {
      clearColdStartAutoReloaded();
    }
    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as ColdStartConfig | undefined;
    const status = error.response?.status;
    const isAuthError = status === 401 || status === 403;
    const isNetworkError = !error.response; // sin respuesta: timeout / red caída
    const sentWithoutAuth = config?._sentWithoutAuth === true;
    const isTransientStatus = status !== undefined && TRANSIENT_STATUSES.has(status);

    // ── Caso 1: cold-start sin token (request se coló antes de restaurar la sesión). ───────────
    // Solo intervenimos en fallos de cold-start (request salió sin token). Cualquier otro fallo
    // —incluido un 401/403 con token presente, que es un fallo REAL— se maneja abajo o se propaga.
    if (config && sentWithoutAuth && (isAuthError || isNetworkError)) {
      const attempts = config._coldStartRetries ?? 0;
      if (attempts < MAX_COLD_START_RETRIES) {
        // Esperar BREVEMENTE a que el token aparezca (login/checkAuth). Si llega, reintentamos.
        const ready = await waitForAuthReady();
        if (ready) {
          config._coldStartRetries = attempts + 1;
          // El retry saldrá CON token (ya no es cold-start). Si por una carrera extrema volviera
          // a salir sin token, el interceptor de request lo re-marca y este flujo se repite,
          // siempre acotado por MAX_COLD_START_RETRIES.
          // Backoff corto incremental, acotado: no es un loop.
          if (attempts > 0) await delay(150 * attempts);
          return instance(config);
        }
      }

      // No hubo token (sesión real ausente) o se agotaron los reintentos: es un transitorio de
      // arranque, NO un fallo real. Marcamos el error para que los `.catch` puedan suprimir el
      // toast. No lo tragamos: el caller decide (un fallo persistente real nunca llega aquí
      // porque esa request sale CON token y no se marca como cold-start).
      (error as TransientAuthError).isTransientAuthColdStart = true;
      // Si seguimos en la ventana de arranque, un cold-start sin token con reintentos agotados
      // también está atascado: último recurso = reload amigable loop-safe (simétrico con el Caso 2).
      // Sin esto, un consumidor de `useFetch` que suprime el error transitorio quedaría en estado
      // vacío silencioso sin recuperación (hallazgo Seer). NO usamos `setError` (mostraría un error
      // técnico al usuario, contra la directiva): la recuperación es el reload amigable, no un toast.
      // Solo si el atasco es GLOBAL (varios endpoints agotados), no por una carga flaky suelta.
      if (isInStartupWindow() && isStartupStuck(config)) {
        handleStuckColdStart();
      }
      return Promise.reject(error);
    }

    // ── Caso 2: clase transitoria REAL (429/500/502/503/504/red) en requests idempotentes. ─────
    // Esta es la causa raíz probada del toast-storm: la avalancha de mount-fetches throttlea al
    // authorizer (429) y API Gateway responde 500 en ráfaga — todo con token. Reintentamos SOLO
    // GET/HEAD (idempotentes; un POST repetido arriesga doble mutación) con backoff + jitter.
    if (config && (isTransientStatus || isNetworkError) && isIdempotentMethod(config)) {
      const attempts = config._transientRetries ?? 0;
      if (attempts < MAX_TRANSIENT_RETRIES) {
        config._transientRetries = attempts + 1;
        // Backoff exponencial + jitter: el 429 significa "bájale"; reintentar sin esperar empeora
        // el throttle. El jitter desincroniza la ráfaga concurrente.
        await delay(transientBackoffMs(attempts));
        return instance(config);
      }

      // Agotados los reintentos. Si seguimos en la ventana de ARRANQUE, esto es un cold-start
      // realmente atascado (las cargas críticas no levantan): último recurso = reload amigable
      // loop-safe. Lo marcamos como transitorio de cold-start para que los `.catch` no spameen un
      // toast técnico mientras la página se recarga.
      if (isInStartupWindow()) {
        // Dentro del arranque el error NO se pinta como toast técnico (los mount-fetches fallan en
        // ráfaga y el usuario vería una lluvia de toasts) — eso vale para cualquier carga.
        (error as TransientAuthError).isTransientAuthColdStart = true;
        // El último recurso (recarga / prompt) solo si el atasco es GLOBAL: un endpoint flaky suelto
        // no justifica recargar la página ni bloquear con un modal un panel que sí funciona.
        if (isStartupStuck(config)) handleStuckColdStart();
        return Promise.reject(error);
      }

      // Fuera de la ventana de arranque: un 500/429 puntual en uso normal NO recarga (sería
      // molesto). Se propaga intacto para que el caller informe como cualquier fallo real.
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

// Solo para tests: limpia el registro de endpoints agotados entre casos (en producción se reinicia
// solo en cada carga de página, que es justo la vida de una ventana de arranque).
export const __resetStartupExhaustionForTests = (): void => {
  exhaustedStartupEndpoints.clear();
};

export default instance;
